import { Input, Select } from '@/components';
import { useReleaseProducts } from '@/features/release-config/hooks';
import { useSearchParams } from '@solidjs/router';
import {
  RELEASE_ENVIRONMENTS,
  RELEASE_STATUSES,
  type ReleaseEnvironment,
  type ReleaseStatus,
} from '@uchat-team-management/shared';
import { For, createEffect, createUniqueId } from 'solid-js';
import { type ReleasesFilters, useReleaseVersions } from './hooks';

type Params = {
  product?: string;
  environment?: string;
  status?: string;
  q?: string;
  groupBy?: string;
};

export const filtersFromParams = (params: Params): ReleasesFilters => ({
  productId: params.product || undefined,
  q: typeof params.q === 'string' ? params.q.trim() || undefined : undefined,
  environment: (RELEASE_ENVIRONMENTS as readonly string[]).includes(params.environment ?? '')
    ? (params.environment as ReleaseEnvironment)
    : undefined,
  status: (RELEASE_STATUSES as readonly string[]).includes(params.status ?? '')
    ? (params.status as ReleaseStatus)
    : undefined,
});

const STORAGE_KEY = 'uchat-release-filters';

export const readStoredParams = (): Params => {
  let parsed: unknown;
  try {
    parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '');
  } catch {
    return {};
  }
  if (typeof parsed !== 'object' || parsed === null) return {};
  const filters = filtersFromParams(parsed as Params);
  return {
    product: typeof filters.productId === 'string' ? filters.productId : undefined,
    environment: filters.environment,
    status: filters.status,
    q: filters.q,
    groupBy: ['version', 'environment'].includes((parsed as Params).groupBy ?? '')
      ? (parsed as Params).groupBy
      : undefined,
  };
};

export const ReleaseFilters = () => {
  const [params, setParams] = useSearchParams<Params>();
  const products = useReleaseProducts();
  const versionListId = createUniqueId();
  const versions = useReleaseVersions(() => {
    const filters = filtersFromParams(params);
    return { productId: filters.productId, environment: filters.environment };
  });

  if (!params.product && !params.environment && !params.status && !params.q && !params.groupBy) {
    const stored = readStoredParams();
    if (stored.product || stored.environment || stored.status || stored.q || stored.groupBy)
      setParams(stored, { replace: true });
  }
  createEffect(() => {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        product: params.product,
        environment: params.environment,
        status: params.status,
        q: params.q,
        groupBy: params.groupBy,
      }),
    );
  });
  return (
    <div class="mb-4 flex flex-wrap gap-3">
      <Select
        value={params.product ?? ''}
        onChange={(v) => setParams({ product: v || undefined })}
        options={[
          { value: '', label: 'All products' },
          ...(products.data?.data ?? []).map((p) => ({ value: p.id, label: p.name })),
        ]}
        class="min-w-44"
      />
      <Select
        value={params.environment ?? ''}
        onChange={(v) => setParams({ environment: v || undefined })}
        options={[
          { value: '', label: 'All environments' },
          ...RELEASE_ENVIRONMENTS.map((ns) => ({ value: ns, label: ns.toUpperCase() })),
        ]}
        class="min-w-40"
      />
      <Select
        value={params.groupBy ?? ''}
        onChange={(v) => setParams({ groupBy: v || undefined })}
        options={[
          { value: '', label: 'No grouping' },
          { value: 'version', label: 'Group by version' },
          { value: 'environment', label: 'Group by environment' },
        ]}
        class="min-w-44"
      />
      <Input
        aria-label="Search version or description"
        type="search"
        maxLength={100}
        placeholder="Search version or description"
        class="min-w-64"
        list={versionListId}
        value={params.q ?? ''}
        onInput={(e) => setParams({ q: e.currentTarget.value || undefined })}
      />
      <datalist id={versionListId}>
        <For each={versions.data?.data ?? []}>{(v) => <option value={v.version} />}</For>
      </datalist>
      <Select
        value={params.status ?? ''}
        onChange={(v) => setParams({ status: v || undefined })}
        options={[
          { value: '', label: 'All statuses' },
          ...RELEASE_STATUSES.map((s) => ({ value: s, label: s })),
        ]}
        class="min-w-36"
      />
    </div>
  );
};
